/**
 * Timebar Store
 *
 * This store does not dehydrate or rehydrate. It is used to communicate
 * capsule regions. The timebar controller computes and updates the regions
 * in this store and the scatter plot controller listens for changes so it
 * can put the regions on scope for the minimap.
 */

import { type SetStoreState, createStore } from '@/stores/createStore';

export interface TimebarRegion {
  conditionId: string | number;
  start: number;
  end: number;
  count: number;
  color?: string;
  opacity?: number;
}

export interface TimebarState {
  regions: TimebarRegion[];
}

const initialState: TimebarState = {
  regions: [],
};

const timebarActionsFactory = (set: SetStoreState<TimebarState>) => ({
  /**
   * Sets the regions for a specified condition, overwriting any previous regions for that condition.
   */
  setRegionsForCondition: (id: string | number, regions: TimebarRegion[]) => {
    set((draft) => {
      // Remove existing regions for this condition
      draft.regions = draft.regions.filter((r) => r.conditionId !== id);
      // Add new regions
      draft.regions.push(...regions);
    });
  },

  /**
   * Clears all the regions
   */
  clearRegions: () => {
    set((draft) => {
      draft.regions = [];
    });
  },

  /**
   * Updates the color of regions belonging to an updated set
   */
  updateRegionColor: (id: string | number, color: string) => {
    set((draft) => {
      draft.regions.forEach((region) => {
        if (region.conditionId === id) {
          region.color = color;
        }
      });
    });
  },
});

const [TimebarStoreProvider, useTimebarStore, useTimebarSelector] = createStore(
  'Timebar',
  initialState,
  timebarActionsFactory,
);

export { TimebarStoreProvider };

/**
 * Get timebar actions
 */
export const useTimebarActions = () => useTimebarStore().actions;

/**
 * Get timebar regions
 */
export const useTimebarRegions = () => useTimebarSelector((state) => state.regions);
